import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const ProfessionalStats = () => {
  const stats = [
    { 
      value: '95%', 
      name: 'Client Satisfaction Rate', 
      description: 'Based on post-incident feedback from businesses across multiple regions.' 
    },
    { 
      value: '24/7/365', 
      name: 'Continuous Monitoring & Support', 
      description: 'Real-time threat detection and response by certified cybersecurity experts.' 
    },
    { 
      value: '99.9%', 
      name: 'System Uptime', 
      description: 'Reliable, secure, and always-on infrastructure protection.' 
    },
    { 
      value: '85%', 
      name: 'Incidents Resolved on First Contact', 
      description: 'Fast and efficient technical support that keeps organizations secure and operational.' 
    }
  ];

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="relative py-24 overflow-hidden bg-white">
      {/* Retaining original background styles */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30"></div>
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `radial-gradient(circle, #1e293b 1px, transparent 1px)`,
            backgroundSize: '32px 32px'
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Retaining original title style */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
            Trusted by Organizations
            <span className="block bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mt-2">
              Worldwide
            </span>
          </h2>
          {/* Added new subtext */}
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mt-4">
            Delivering enterprise-grade cybersecurity and 24/7 support trusted by organizations across Africa, the MENA region, and beyond.
          </p>
        </motion.div>

        {/* Using a responsive 4-column grid and preserving card styles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 max-w-7xl mx-auto">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ 
                duration: 0.8, 
                delay: index * 0.2,
                type: "spring",
                stiffness: 100,
                damping: 15
              }}
              className="text-center group"
            >
              {/* Using original value styling, but with new static content */}
              <div className="mb-4">
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-cyan-500 group-hover:text-blue-600 transition-colors duration-500">
                  {stat.value}
                </div>
              </div>

              {/* Using original label styling for the name */}
              <h3 className="text-sm md:text-base text-gray-700 font-medium leading-tight mb-2 h-12">
                {stat.name}
              </h3>
              
              {/* Added description text */}
              <p className="text-xs text-gray-500">
                  {stat.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProfessionalStats;